import { useCallback, useState } from "react";
import { VideoController } from "./useVideoController";

/** What an armed range will be applied to on commit. */
export type ArmTarget =
  | { kind: "entry"; uid: string }
  | { kind: "new"; speaker: string; text: string };

export interface ArmedState {
  target: ArmTarget;
  start: number | null;
  end: number | null;
}

interface ArmingDeps {
  controller: VideoController;
  setEntryRange: (uid: string, start: number, end: number) => void;
  addEntry: (input: {
    label: string;
    start: number;
    end: number;
    text: string;
  }) => string;
}

/**
 * Coordinates "drop a marker" range selection shared between the video pane
 * (the TimeRangeSelector) and the transcript pane (per-entry "Set range" and
 * the add-entry composer). Holds the in-progress start/end until committed.
 */
export const useRangeArming = ({
  controller,
  setEntryRange,
  addEntry,
}: ArmingDeps) => {
  const [armed, setArmed] = useState<ArmedState | null>(null);

  const armEntry = useCallback((uid: string, start: number, end: number) => {
    setArmed({ target: { kind: "entry", uid }, start, end });
  }, []);

  const armNew = useCallback(
    (speaker: string, text: string, start?: number) => {
      // When a start is given (e.g. inserting after a prior entry), seek there
      // so the user only needs to scrub forward to drop the end marker.
      if (start != null) controller.seekTo(start);
      setArmed({
        target: { kind: "new", speaker, text },
        start: start ?? controller.currentTime,
        end: null,
      });
    },
    [controller],
  );

  // No value → use the current playhead ("set to playhead"); a value → typed input.
  const setStart = useCallback(
    (value?: number) => {
      setArmed((a) =>
        a ? { ...a, start: value ?? controller.currentTime } : a,
      );
    },
    [controller],
  );

  const setEnd = useCallback(
    (value?: number) => {
      setArmed((a) => (a ? { ...a, end: value ?? controller.currentTime } : a));
    },
    [controller],
  );

  const cancel = useCallback(() => setArmed(null), []);

  const canCommit =
    armed?.start != null &&
    armed?.end != null &&
    armed.start !== armed.end;

  const commit = useCallback(() => {
    if (armed?.start == null || armed?.end == null || armed.start === armed.end) {
      return;
    }
    const start = Math.min(armed.start, armed.end);
    const end = Math.max(armed.start, armed.end);
    // Clear local state via the updater (safe to double-invoke), then run the
    // store mutations exactly once outside it — StrictMode double-invokes the
    // setArmed callback, so a mutation there would create duplicate entries.
    setArmed(null);
    if (armed.target.kind === "entry") {
      setEntryRange(armed.target.uid, start, end);
    } else {
      addEntry({
        label: armed.target.speaker,
        start,
        end,
        text: armed.target.text,
      });
    }
  }, [armed, setEntryRange, addEntry]);

  const armedUid =
    armed?.target.kind === "entry" ? armed.target.uid : null;

  return {
    armed,
    armedUid,
    canCommit,
    armEntry,
    armNew,
    setStart,
    setEnd,
    cancel,
    commit,
  };
};
